// The shift leader's screen: the second signature.
//
// Sixty items were walked. This screen exists so the person signing them off
// looks at the six that matter rather than scrolling past all sixty — because a
// verification that is a scroll and a tap is the formality both schemes are
// trying to prevent when they ask for a second person.
//
// FOUR THINGS THAT ARE DELIBERATE.
//
// 1. IT DOES NOT USE THE OFFLINE QUEUE, and this is the one screen in the
//    operator shell that does not. A verification made offline would be an
//    attestation about a reading the server has not accepted yet — possibly one
//    it will refuse. Capture is optimistic because the operator saw the thing;
//    verification is not, because the verifier is attesting to a RECORD, and the
//    record has to exist first.
//
// 2. IT IS ORDERED BY ATTENTION, not by the form. quality_readiness already sorts
//    failures, missing comments and critical control points to the top, and this
//    screen renders that order as given — the opposite of the walk-around, which
//    re-sorts into the spec's physical order. Two audiences, two orders.
//
// 3. THE ACTIONS ARE HIDDEN WITHOUT A VERIFY GRANT, and the server refuses again
//    regardless. A hidden button is a courtesy; the gate is the control.
//
// 4. SELF-VERIFICATION IS SHOWN AS REFUSED RATHER THAN HIDDEN. The person who
//    captured a check needs to understand why they cannot sign it, not wonder
//    why a button is missing on one row out of sixty.
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { usePageTitle } from '../../lib/pageTitle';
import { attentionLabel } from '../../lib/vocabulary';
import { currentUpn } from '../../config/auth';
import { opFocus, opMessage } from '../../shells/OperatorShell';
import { PilotBadge } from '../../components/Pilot';
import type { ReadinessItem, ReadinessPayload } from './Walkaround';
import { SideBySide } from './LinePhotos';
import { evictReferencePhotos, listedReferences } from './linePhotoModel';

/** GET /authorisations?upn=&activeOnly=true — the register, not a token claim. */
interface Grant {
  action: string;
}

function sameUpn(a: string | null | undefined, b: string | null | undefined): boolean {
  return (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();
}

/** One group on the list. `open` = expanded by default. */
export interface VerifyGroup {
  key: 'put_right' | 'awaiting' | 'unanswered' | 'done';
  title: string;
  open: boolean;
  items: ReadinessItem[];
}

/**
 * The list, grouped by what the verifier can do (UI/UX audit, 31 Oct 2026, UX-05).
 *
 * ⚠ A FILTER, NEVER A SORT: each group keeps the server's attention order, which is
 * deliberate (point 2 at the top of this file). The groups only stop 19 unanswered
 * cards standing between a shift leader and the one answer waiting for them.
 *
 * Order of the tests matters: MET and WAIVED are done whatever reading they carry;
 * AWAITING_VERIFY is waiting; any other answered item (a failing answer, or one a
 * verifier failed back to OWED) needs putting right and comes FIRST, because it is
 * what is actually stopping the line; the rest have no answer yet. Empty groups
 * are left out.
 */
export function groupItems(items: ReadinessItem[]): VerifyGroup[] {
  const putRight: ReadinessItem[] = [];
  const awaiting: ReadinessItem[] = [];
  const unanswered: ReadinessItem[] = [];
  const done: ReadinessItem[] = [];
  for (const it of items) {
    if (it.status === 'MET' || it.status === 'WAIVED') done.push(it);
    else if (it.status === 'AWAITING_VERIFY') awaiting.push(it);
    else if (it.reading !== null) putRight.push(it);
    else unanswered.push(it);
  }
  const all: VerifyGroup[] = [
    { key: 'put_right', title: 'Failed - needs putting right', open: true, items: putRight },
    { key: 'awaiting', title: 'Waiting for a signature', open: true, items: awaiting },
    { key: 'unanswered', title: 'Not answered yet', open: false, items: unanswered },
    { key: 'done', title: 'Verified or waived', open: false, items: done },
  ];
  return all.filter((g) => g.items.length > 0);
}

export function Verify() {
  const { runId } = useParams<{ runId: string }>();
  const me = currentUpn();
  // The tab says what this screen is for (UI/UX audit, 31 Oct 2026, UX-21): "Verify run 2 · Line check · Quality".
  usePageTitle(runId ? `Verify run ${runId}` : null);

  const [data, setData] = useState<ReadinessPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState<number | null>(null);
  /** true = holds VERIFY, false = does not, null = NOT KNOWN — either still
   *  reading the register or the read failed. Never collapse null into false. */
  const [canVerify, setCanVerify] = useState<boolean | null>(null);
  /** The register could not be read. A separate state from `canVerify` because
   *  "we could not ask" is not an answer about the person. */
  const [grantError, setGrantError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!runId) return;
    setError(null);
    api
      .get(`/runs/${runId}/readiness`)
      .then((d: unknown) => {
        const payload = d as ReadinessPayload;
        setData(payload);
        const listed = listedReferences((payload.items ?? []).map((it) => it.parameter.referencePhotos));
        if (listed) void evictReferencePhotos('readiness', listed);
      })
      .catch((e: unknown) => setError(opMessage(e)));
  }, [runId]);

  useEffect(load, [load]);

  // Authority is a READ OF THE REGISTER, the same as the office shell does it. A
  // directory role says something about a job; dbo.Authorisation says who
  // granted what, from when, until when — which is what an auditor asks for.
  //
  // ⚠⚠ THREE STATES, NOT TWO, AND THE THIRD IS WHY THIS IS NOT A ONE-LINER.
  // This used to be `.catch(() => setCanVerify(false))`, which files "the
  // register could not be read" under "you do not hold VERIFY". The screen then
  // told a shift leader who DOES hold it that they do not, hid Pass and Fail on
  // every item, and sent them to find a QA manager — at a changeover, with the
  // line unreleased and product checks being refused behind them. The effect
  // depends on `me` alone and "Try again" only refetched the readiness list, so
  // nothing short of a full reload could correct it.
  //
  // A failed read is not a fact about a person. `grantError` carries it, the
  // actions stay hidden because we genuinely do not know, and the difference is
  // said out loud with a control that re-asks.
  const readGrants = useCallback(() => {
    if (!me) return;
    setGrantError(null);
    setCanVerify(null);
    api
      .get(`/authorisations?upn=${encodeURIComponent(me)}&activeOnly=true`)
      .then((d: unknown) => {
        const rows = Array.isArray(d) ? (d as Grant[]) : [];
        setCanVerify(rows.some((g) => g.action === 'VERIFY'));
      })
      .catch((e: unknown) => setGrantError(opMessage(e)));
  }, [me]);

  useEffect(readGrants, [readGrants]);

  const verify = useCallback(
    async (item: ReadinessItem, outcome: 'PASS' | 'FAIL') => {
      if (!item.reading) return;
      const note = (notes[item.obligationId] ?? '').trim();
      if (outcome === 'FAIL' && note === '') {
        setError(`A failed verification of "${item.parameter.name}" needs a note.`);
        return;
      }
      setBusy(item.obligationId);
      setError(null);
      try {
        await api.post(`/readings/${item.reading.id}/verify`, {
          outcome,
          note: note === '' ? null : note,
        });
        load();
      } catch (e: unknown) {
        setError(opMessage(e));
      } finally {
        setBusy(null);
      }
    },
    [notes, load],
  );

  if (error && !data) {
    return (
      <div className="p-(--s5)">
        <p role="alert" className="m-0 text-(length:--fs-lg) font-bold text-(--warn)">
          {error}
        </p>
        {/* Retries BOTH reads. When the screen failed outright the network was
            the cause, and the register read almost certainly failed with it —
            retrying only the list left signing disabled with nothing saying why. */}
        <button
          type="button"
          onClick={() => {
            load();
            readGrants();
          }}
          className={`mt-(--s4) min-h-[56px] rounded-(--radius) border border-(--border-strong) bg-(--card) px-(--s4) text-(length:--fs-md) font-semibold ${opFocus}`}
        >
          Try again
        </button>
      </div>
    );
  }

  if (!data) {
    return <p role="status" className="p-(--s5) text-(length:--fs-md)">Loading…</p>;
  }

  const pending = data.items.filter((i) => i.status === 'AWAITING_VERIFY');
  // ⚠ "AWAITING YOUR SIGNATURE" COUNTS ONLY WHAT THIS PERSON CAN SIGN (UI/UX audit,
  // 31 Oct 2026, UX-05). It used to count every AWAITING_VERIFY item, so an
  // operator who answered the one check waiting read "1 awaiting your signature"
  // and then found it said "You captured this check, so you cannot verify it".
  // Their own answers are said separately; rule 9 still refuses them server-side.
  // With the register unread (canVerify null) or no grant, nothing here is the
  // viewer's to sign, so the count is said about the run, not the person (rule 15).
  const ownPending = pending.filter((i) => sameUpn(i.reading?.capturedByUpn, me));
  const forMe = pending.length - ownPending.length;
  const groups = groupItems(data.items);

  // ⚠ A WAY OFF THIS SCREEN (UI/UX audit, 31 Oct 2026, UX-42). It had no link at all: a shift
  // leader who signed the last check, or opened the wrong run, had only the shell's area tabs.
  // "‹ Lines" goes to this run's line (UX-52's `/op?line=CODE`), "Back to run N" to the run. Once
  // the line is released the run is the next job, so that link becomes the filled one and says so.
  const lineCode = data.run?.line.code ?? null;
  const linesHref = lineCode ? `/op?line=${encodeURIComponent(lineCode)}` : '/op';
  const LINK = `flex min-h-[56px] items-center rounded-(--radius) border border-(--border-strong) px-(--s4) text-(length:--fs-md) font-semibold ${opFocus}`;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <nav aria-label="Leave verify" className="flex flex-none flex-wrap items-center gap-(--s3) border-b border-(--border) bg-(--card) px-(--s4) py-(--s2) sm:px-(--s5)">
        <Link to={linesHref} className={`${LINK} bg-(--card)`}>
          ‹ Lines
        </Link>
        {runId ? (
          data.released ? (
            <Link to={`/op/run/${runId}`} className={`${LINK} bg-(--primary) font-bold text-(--primary-fg)`}>
              Line released: back to the run
            </Link>
          ) : (
            <Link to={`/op/run/${runId}`} className={`${LINK} bg-(--card)`}>
              Back to run {runId}
            </Link>
          )
        ) : null}
      </nav>
      <div className="border-b border-(--border) p-(--s5)">
        <h1 className="m-0 flex flex-wrap items-center gap-(--s3) text-(length:--fs-xl) font-bold">
          Verify start-up{data.run ? ` — ${data.run.line.name}` : ''}
          {/* Default size (UI/UX audit, 31 Oct 2026, UX-11): the badge marks the
              record; a large one under a sentence repeated what the badge says. */}
          <PilotBadge isPilot={data.isPilot} />
        </h1>
        {/* Rule 27: the only pilot sentence kept is "pilot mode has ended", when the
            run no longer takes entries (UI/UX audit, 31 Oct 2026, UX-11). */}
        {data.isPilot && data.pilot?.acceptsNewEntries === false ? (
          <p className="m-0 mt-(--s2) text-(length:--fs-md) font-semibold">
            Pilot mode has ended: this run takes no new verifications.
          </p>
        ) : null}
        <p className="m-0 mt-(--s2) text-(length:--fs-md) text-(--muted)">
          {canVerify === true ? (
            <>
              {forMe} awaiting your signature
              {ownPending.length > 0
                ? ` · ${ownPending.length} you answered, waiting for another shift leader`
                : ''}
            </>
          ) : (
            <>{pending.length} awaiting a second signature</>
          )}{' '}
          · {data.counts.outstanding} outstanding of {data.counts.total}.
        </p>
        {data.released ? (
          <p className="m-0 mt-(--s2) text-(length:--fs-md) font-semibold text-(--ok)">
            This line is released. Product checks can be captured.
          </p>
        ) : null}
        {/* ⚠ THE COULD-NOT-READ CASE COMES FIRST AND SAYS SOMETHING DIFFERENT.
            Sending somebody to a QA manager for a grant they already hold wastes
            a changeover; saying the register could not be read, and offering to
            ask again, is recoverable where a full page reload was not. */}
        {grantError ? (
          <div className="m-0 mt-(--s2) flex flex-wrap items-center gap-(--s3)">
            <p className="m-0 text-(length:--fs-md) font-semibold text-(--warn)">
              Your authorisations could not be read, so signing is unavailable until we know. This
              is not a statement that you lack the grant. {grantError}
            </p>
            <button
              type="button"
              onClick={readGrants}
              className={`min-h-[56px] rounded-(--radius) border border-(--border-strong) bg-(--card) px-(--s4) text-(length:--fs-md) font-semibold ${opFocus}`}
            >
              Check my authorisations again
            </button>
          </div>
        ) : canVerify === false ? (
          <p className="m-0 mt-(--s2) text-(length:--fs-md) font-semibold text-(--warn)">
            You do not hold a current VERIFY authorisation, so you can read this list but not sign
            it. Ask a QA manager to grant one.
          </p>
        ) : null}
      </div>

      {error ? (
        <div
          role="alert"
          className="m-(--s4) rounded-(--radius) border border-(--warn-line) bg-(--warn-bg) p-(--s4) text-(length:--fs-md) font-semibold text-(--warn)"
        >
          {error}
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto p-(--s4)">
        {/* Grouped by what the verifier can do with each item (UI/UX audit, 31 Oct
            2026, UX-05): the answers to put right, then the answers waiting for a
            signature, then - collapsed - the unanswered and the done. Within a
            group the server's attention order stands (point 2 above). The first
            two open by default; the last two only when nothing comes before
            them, so a run with nothing answered yet still shows its checks. */}
        {groups.map((g, gi) => (
          <details key={g.key} open={g.open || gi === 0} className="mt-(--s3)">
            <summary
              className={`flex min-h-[56px] cursor-pointer items-center text-(length:--fs-lg) font-bold ${opFocus}`}
            >
              {g.title} ({g.items.length})
            </summary>
            <ul className="m-0 list-none p-0">
              {g.items.map((it) => {
                const mine = sameUpn(it.reading?.capturedByUpn, me);
                const actionable = it.status === 'AWAITING_VERIFY' && canVerify === true && !mine;
                // ⚠ THE NULL CHECK IS THE POINT. `it.reading?.verdict` is `undefined`
                // on an item nobody has answered yet, and `undefined !== null` is
                // true — so an earlier version painted every UNANSWERED item red.
                // On a screen whose whole job is showing a verifier which six of
                // sixty items to walk over and look at, thirty false failures is
                // worse than no triage at all.
                const answeredYet = it.reading !== null;
                const failed = answeredYet && it.reading!.verdict !== 'IN';
                return (
                  <li
                    key={it.obligationId}
                    className={`mt-(--s3) rounded-(--radius) border p-(--s4) ${
                      failed
                        ? 'border-(--bad-line) bg-(--bad-bg)'
                        : answeredYet
                          ? 'border-(--border) bg-(--card)'
                          : // Not answered is not failed, and must not look like it. No
                            // opacity (UI/UX audit, 31 Oct 2026, UX-52): opacity-70 took the
                            // text to about 3.2:1; "not answered yet" on the card says it.
                            'border-(--border) bg-(--subtle)'
                    }`}
                  >
                    {/* ⚠ min-w-0 ON THE TEXT COLUMN. A flex child's default
                        min-width is auto, so a long check name ("Metal detector
                        tests are working. Ferrous, Non-Ferrous, Stainless Steel")
                        refuses to shrink and pushes the row wider than the screen.
                        On a phone — which is the device this screen is FOR, since
                        the shift leader verifies from their own — that clipped the
                        right-hand edge off every row including the verdict. */}
                    <div className="flex flex-wrap items-start justify-between gap-(--s3)">
                      <div className="min-w-0 flex-1 basis-[16rem]">
                        <p className="m-0 text-(length:--fs-md) font-semibold">
                          {it.parameter.name}
                        </p>
                        <p className="m-0 mt-(--s1) wrap-break-word font-mono text-(length:--fs-sm) text-(--muted)">
                          {it.parameter.section ?? ''}
                          {answeredYet ? (
                            <>
                              {' · answered '}
                              <strong>{String(it.reading!.answer)}</strong>
                              {it.reading!.capturedByUpn ? ` by ${it.reading!.capturedByUpn}` : ''}
                            </>
                          ) : (
                            ' · not answered yet'
                          )}
                        </p>
                        {it.reading?.evidenceRef ? (
                          <p className="m-0 mt-(--s2) text-(length:--fs-md)">
                            {it.reading.evidenceRef}
                          </p>
                        ) : null}
                      </div>
                      {/* The triage flags, from the API, in words. `no_evidence_on_fail`
                          is the one worth walking over to look at: something failed
                          and nobody wrote down what. */}
                      {it.attention.length ? (
                        <ul className="m-0 flex list-none flex-wrap gap-(--s1) p-0">
                          {it.attention.map((f) => (
                            <li
                              key={f}
                              className="rounded-(--radius) bg-(--primary-soft) px-(--s2) text-(length:--fs-sm)"
                            >
                              {/* A sentence, not the API code with its underscores spaced
                                  (UI/UX audit, 31 Oct 2026, UX-18). */}
                              {attentionLabel(f)}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
    
                    {/* Reference | What was seen. Evidence beside the check, never a judgement. */}
                    <SideBySide
                      specParameterId={it.parameter.id}
                      label={it.parameter.name}
                      reference={it.parameter.referencePhotos}
                      readingId={it.reading ? it.reading.id : null}
                      seen={it.reading?.photos}
                    />
    
                    {it.verification ? (
                      <p className="m-0 mt-(--s3) text-(length:--fs-sm) text-(--muted)">
                        {it.verification.outcome} by {it.verification.verifiedByUpn}{' '}
                        <PilotBadge isPilot={it.verification.isPilot} />
                      </p>
                    ) : null}
    
                    {mine && it.status === 'AWAITING_VERIFY' ? (
                      <p className="m-0 mt-(--s3) text-(length:--fs-sm) font-semibold text-(--warn)">
                        You captured this check, so you cannot verify it. A verification is a second
                        person confirming the first.
                      </p>
                    ) : null}
    
                    {actionable ? (
                      <>
                        <label className="mt-(--s3) block text-(length:--fs-sm) text-(--muted)">
                          Note (required to fail)
                          <input
                            value={notes[it.obligationId] ?? ''}
                            onChange={(e) =>
                              setNotes((n) => ({ ...n, [it.obligationId]: e.target.value }))
                            }
                            className="mt-(--s1) block min-h-[48px] w-full rounded-(--radius) border border-(--border-strong) bg-(--bg-input) px-(--s3) text-(length:--fs-md)"
                          />
                        </label>
                        <div className="mt-(--s3) flex gap-(--s3)">
                          <button
                            type="button"
                            disabled={busy === it.obligationId}
                            onClick={() => verify(it, 'PASS')}
                            className={`min-h-[56px] flex-1 rounded-(--radius) border border-(--ok-line) bg-(--ok-bg) text-(length:--fs-lg) font-bold text-(--ok) disabled:opacity-50 ${opFocus}`}
                          >
                            Pass
                          </button>
                          <button
                            type="button"
                            disabled={busy === it.obligationId}
                            onClick={() => verify(it, 'FAIL')}
                            className={`min-h-[56px] flex-1 rounded-(--radius) border border-(--bad-line) bg-(--bad-bg) text-(length:--fs-lg) font-bold text-(--bad) disabled:opacity-50 ${opFocus}`}
                          >
                            Fail
                          </button>
                        </div>
                      </>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </details>
        ))}

        {data.counts.total === 0 ? (
          <p className="text-(length:--fs-md) text-(--muted)">
            This line has no approved start-up form, so there is nothing to verify. That is not the
            same as passing — nothing was asked.
          </p>
        ) : null}
      </div>
    </div>
  );
}

export default Verify;
